import { useMemo, useRef, useState } from "react";
import { useTranslation } from "@i18n";
import ModalStateDialogComponent from "@CubeComponents/html/modal/ModalWithState";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import UserNameTagComponent from "../../../cubeComponent/html/UserNameTagComponent";
import IconComponent from "@CubeComponents/icons";
import { toObjectIdString } from "@Helpers/schemas/common.schema";
import { usePortalChat } from "./context";
import { VirtualizedList } from "./VirtualizedList";
import { useChatDirectory } from "../hooks/useChatDirectory";
import {
  PICKER_ROW_HEIGHT,
  GROUP_NAME_MAX_LENGTH,
  TOPIC_MAX_LENGTH,
  SECTION_NAME_MAX_LENGTH,
  SEARCH_MAX_LENGTH,
} from "../portalChat.constants";
import {
  createConversationRequest,
  createSectionRequest,
  renameSectionRequest,
  deleteSectionRequest,
  reorderSectionsRequest,
  updateProjectOrderRequest,
  type ChatConversation,
  type ChatDirectoryUser,
} from "../api/portalChat.api";

/**
 * Pointer-based sortable rows (PRD §4.9: reorder is DRAG-ONLY and touch-drag must work — site
 * users are on tablets). Pointer events unify mouse and touch without a dependency.
 */
function useDragReorder(onDrop: (from: number, to: number) => void) {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Returns the INSERTION slot (0..rows.length): a pointer below the last row's midpoint means
  // "insert at the end" — clamping to the last row index made every downward drop land one slot
  // below the indicator once the dragged row was removed from the array.
  const indexFromPointer = (clientY: number): number | null => {
    const container = containerRef.current;
    if (!container) return null;
    const rows = [...container.querySelectorAll<HTMLElement>("[data-drag-row]")];
    for (const [i, row] of rows.entries()) {
      const rect = row.getBoundingClientRect();
      if (clientY < rect.top + rect.height / 2) return i;
    }
    return rows.length ? rows.length : null;
  };

  const handleProps = (index: number) => ({
    onPointerDown: (e: React.PointerEvent<HTMLElement>) => {
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      setDragIndex(index);
      setOverIndex(index);
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (dragIndex === null) return;
      const target = indexFromPointer(e.clientY);
      if (target !== null) setOverIndex(target);
    },
    onPointerUp: () => {
      if (dragIndex !== null && overIndex !== null) {
        // The insertion slot was computed BEFORE removal — dropping below the dragged row must
        // shift down by one after the splice-out, or every downward drop lands one row too far.
        const to = overIndex > dragIndex ? overIndex - 1 : overIndex;
        if (to !== dragIndex) onDrop(dragIndex, to);
      }
      setDragIndex(null);
      setOverIndex(null);
    },
    style: { touchAction: "none" as const, cursor: "grab" as const },
  });

  return { containerRef, handleProps, dragIndex, overIndex };
}

/** People picker + group naming for starting a direct or group chat (PRD §4.2). */
export function NewChatDialog({ onClose }: { readonly onClose: () => void }) {
  const { t } = useTranslation();
  const chat = usePortalChat();
  const [kind, setKind] = useState<"direct" | "group">("direct");
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<ChatDirectoryUser[]>([]);
  const [name, setName] = useState("");
  const [topic, setTopic] = useState("");
  const [creating, setCreating] = useState(false);
  const [duplicate, setDuplicate] = useState<ChatConversation | null>(null);
  // Server-side search + pagination — the whole portal directory never loads at once.
  const { users: filtered, loading, loadMore } = useChatDirectory(search);

  const toggleUser = (user: ChatDirectoryUser) => {
    setSelected((prev) => {
      const has = prev.some((u) => String(u._id) === String(user._id));
      if (has) return prev.filter((u) => String(u._id) !== String(user._id));
      if (kind === "direct") return [user];
      return [...prev, user];
    });
  };

  const create = async (force = false) => {
    if (!selected.length || creating) return;
    setCreating(true);
    try {
      const response = await createConversationRequest({
        type: kind,
        memberIds: selected.map((user) => user._id),
        ...(kind === "group" && name.trim() ? { name: name.trim() } : {}),
        ...(kind === "group" && topic.trim() ? { topic: topic.trim() } : {}),
        ...(force ? { force: true } : {}),
      });
      if (!response.data.status) {
        chat.showServerError(response.data.errorMessage, t("Could not create the chat"));
        return;
      }
      if (response.data.duplicateOf) {
        setDuplicate(response.data.duplicateOf);
        return;
      }
      const created = response.data.result;
      await chat.refreshList();
      if (created) chat.selectConversation(created._id);
      onClose();
    } finally {
      setCreating(false);
    }
  };

  if (duplicate) {
    return (
      <ModalStateDialogComponent
        isVisible
        width="26rem"
        headerTitle={t("Similar group exists")}
        onCancel={() => setDuplicate(null)}
        modelContent={
          <div className="text-sm">
            {t("A group with exactly these members already exists: {{name}}. Open it, or create a new one anyway?", {
              name: duplicate.name,
            })}
          </div>
        }
        footerContent={
          <div className="flex justify-end gap-2">
            <ButtonComponent
              buttonType="borderButton"
              onClick={() => {
                chat.selectConversation(duplicate._id);
                onClose();
              }}
            >
              {t("Open existing")}
            </ButtonComponent>
            <ButtonComponent buttonType="gradientButton" onClick={() => void create(true)} disabled={creating}>
              {t("Create new")}
            </ButtonComponent>
          </div>
        }
      />
    );
  }

  return (
    <ModalStateDialogComponent
      isVisible
      width="30rem"
      headerTitle={t("New chat")}
      onCancel={onClose}
      modelContent={
        <div className="flex max-h-[60vh] w-full flex-col gap-2">
          <div className="flex gap-1 rounded-xs bg-[#f0f5f9] p-0.5">
            {(["direct", "group"] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => {
                  setKind(option);
                  setSelected((prev) => (option === "direct" ? prev.slice(0, 1) : prev));
                }}
                className={`flex-1 rounded-xs px-2 py-1 text-sm ${kind === option ? "bg-white font-semibold shadow-2xs" : ""}`}
              >
                {option === "direct" ? t("Direct") : t("Group")}
              </button>
            ))}
          </div>
          {kind === "group" && (
            <>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={GROUP_NAME_MAX_LENGTH}
                placeholder={t("Group name (optional)")}
                className="w-full rounded-xs border border-[#dce6f1] px-2 py-1 text-sm outline-hidden"
              />
              <input
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                maxLength={TOPIC_MAX_LENGTH}
                placeholder={t("Topic (optional)")}
                className="w-full rounded-xs border border-[#dce6f1] px-2 py-1 text-sm outline-hidden"
              />
            </>
          )}
          {selected.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {selected.map((user) => (
                <span
                  key={String(user._id)}
                  className="flex items-center gap-1 rounded-full bg-[#e8f1fc] px-2 py-0.5 text-xs"
                >
                  {user.fullName}
                  <span role="button" tabIndex={0} onClick={() => toggleUser(user)} onKeyDown={() => undefined}>
                    <IconComponent name="CrossIcon" width="0.6rem" height="0.6rem" />
                  </span>
                </span>
              ))}
            </div>
          )}
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            maxLength={SEARCH_MAX_LENGTH}
            placeholder={t("Search people")}
            className="w-full rounded-xs border border-[#dce6f1] px-2 py-1 text-sm outline-hidden"
          />
          <div className="h-64">
            {loading && !filtered.length && <div className="p-3 text-sm text-gray-400">{t("Loading")}…</div>}
            {!loading && !filtered.length && (
              <div className="p-3 text-sm text-gray-400">{t("No members match your search.")}</div>
            )}
            {filtered.length > 0 && (
              <VirtualizedList
                items={filtered}
                rowHeight={PICKER_ROW_HEIGHT}
                onEndReached={loadMore}
                ariaLabel={t("Search people")}
                renderRow={(user) => {
                  const isSelected = selected.some((u) => String(u._id) === String(user._id));
                  return (
                    <button
                      type="button"
                      onClick={() => toggleUser(user)}
                      className={`flex h-full w-full items-center gap-2 px-2 text-start hover:bg-[#f0f5f9] ${
                        isSelected ? "bg-[#e8f1fc]" : ""
                      }`}
                    >
                      <UserNameTagComponent showAvatarOnly id={String(user._id)} size="XS" isCircle />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm">{user.fullName}</span>
                        <span className="block truncate text-xs text-gray-400">
                          {user.email}
                          {user.organisationName ? ` · ${user.organisationName}` : ""}
                        </span>
                      </span>
                      {isSelected && <IconComponent name="CheckBoxIcon" width="1rem" height="1rem" />}
                    </button>
                  );
                }}
              />
            )}
          </div>
        </div>
      }
      footerContent={
        <div className="flex justify-end gap-2">
          <ButtonComponent buttonType="borderButton" onClick={onClose}>
            {t("Cancel")}
          </ButtonComponent>
          <ButtonComponent
            buttonType="gradientButton"
            onClick={() => void create()}
            disabled={!selected.length || creating}
          >
            {kind === "direct" ? t("Start chat") : t("Create group")}
          </ButtonComponent>
        </div>
      }
    />
  );
}

/** Manage sections dialog (PRD §4.9): scope switcher, counts, drag-only reorder, inline rename/delete. */
export function SectionsDialog({
  initialScope,
  onClose,
}: {
  readonly initialScope: "direct" | "group";
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const chat = usePortalChat();
  const [scope, setScope] = useState<"direct" | "group">(initialScope);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const sections = chat.sections.filter((s) => s.scope === scope).sort((a, b) => a.order - b.order);

  // PRD §4.9: counts reflect VISIBLE chats only — hidden chats still belong but are not counted.
  const chatCount = (sectionId: string) =>
    chat.conversations.filter((c) => !c.hidden && String(c.sectionId ?? "") === sectionId).length;

  const run = async (action: () => Promise<unknown>) => {
    if (busy) return;
    setBusy(true);
    try {
      await action();
      await chat.refreshList();
    } finally {
      setBusy(false);
    }
  };

  const { containerRef, handleProps, dragIndex, overIndex } = useDragReorder((from, to) => {
    const ids = sections.map((s) => s._id);
    const [moved] = ids.splice(from, 1);
    if (moved === undefined) return;
    ids.splice(to, 0, moved);
    void run(() => reorderSectionsRequest({ scope, sectionIds: ids }));
  });

  const createSection = () => {
    const name = newName.trim();
    if (!name) return;
    void run(async () => {
      await createSectionRequest({ name, scope });
      chat.showToast(t("Section {{name}} created", { name }));
    });
    setNewName("");
  };

  return (
    <ModalStateDialogComponent
      isVisible
      width="28rem"
      headerTitle={t("Manage sections")}
      onCancel={onClose}
      footerContent={<></>}
      modelContent={
        <div className="flex max-h-[60vh] w-full flex-col gap-2">
          <div className="flex gap-1 rounded-xs bg-[#f0f5f9] p-0.5">
            {(["direct", "group"] as const).map((option) => (
              <button
                key={option}
                type="button"
                onClick={() => setScope(option)}
                className={`flex-1 rounded-xs px-2 py-1 text-sm ${scope === option ? "bg-white font-semibold shadow-2xs" : ""}`}
              >
                {option === "direct" ? t("Direct") : t("Group")}
              </button>
            ))}
          </div>
          <div className="text-xs text-gray-500">
            {t("Sections are private to you. Chats not in a section always sit above your sections.")}{" "}
            {t("Drag a section by its handle to reorder.")}
          </div>
          <div className="flex gap-2">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              maxLength={SECTION_NAME_MAX_LENGTH}
              placeholder={t("New section name")}
              className="min-w-0 flex-1 rounded-xs border border-[#dce6f1] px-2 py-1 text-sm outline-hidden"
              onKeyDown={(e) => {
                if (e.key === "Enter") createSection();
              }}
            />
            <ButtonComponent buttonType="borderButton" disabled={!newName.trim() || busy} onClick={createSection}>
              {t("Create new section")}
            </ButtonComponent>
          </div>
          <div ref={containerRef} className="min-h-0 flex-1 overflow-y-auto">
            {!sections.length && (
              <div className="flex flex-col items-center gap-1 p-4 text-center">
                <span className="text-sm font-semibold text-gray-500">{t("No sections yet")}</span>
                <span className="text-xs text-gray-400">
                  {t("Create a section to group these chats — Priority, Handover, anything you like. Only you see it.")}
                </span>
              </div>
            )}
            {sections.map((section, index) => (
              <div
                key={String(section._id)}
                data-drag-row
                className={`flex items-center gap-2 border-b border-[#f0f5f9] px-1 py-1.5 ${
                  dragIndex === index ? "opacity-50" : ""
                } ${overIndex === index && dragIndex !== null && dragIndex !== index ? "border-t-2 border-t-[#6BBBE3]" : ""} ${
                  dragIndex !== null && overIndex !== null && index === overIndex - 1 && overIndex > dragIndex + 1
                    ? "border-b-2 border-b-[#6BBBE3]"
                    : ""
                }`}
              >
                <span {...handleProps(index)} title={t("Drag a section by its handle to reorder.")}>
                  <IconComponent name="threedots" width="0.9rem" height="0.9rem" className="rotate-90 opacity-50" />
                </span>
                {editing?.id === String(section._id) ? (
                  <>
                    <input
                      value={editing.name}
                      onChange={(e) => setEditing({ id: editing.id, name: e.target.value })}
                      maxLength={SECTION_NAME_MAX_LENGTH}
                      className="min-w-0 flex-1 rounded-xs border border-[#dce6f1] px-2 py-0.5 text-sm outline-hidden"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && editing.name.trim()) {
                          void run(() =>
                            renameSectionRequest({
                              sectionId: toObjectIdString(editing.id),
                              name: editing.name.trim(),
                            }),
                          );
                          setEditing(null);
                        }
                        if (e.key === "Escape") setEditing(null);
                      }}
                    />
                    <ButtonComponent
                      buttonType="borderButton"
                      disabled={!editing.name.trim() || busy}
                      onClick={() => {
                        void run(() =>
                          renameSectionRequest({ sectionId: toObjectIdString(editing.id), name: editing.name.trim() }),
                        );
                        setEditing(null);
                      }}
                    >
                      {t("Save")}
                    </ButtonComponent>
                  </>
                ) : (
                  <>
                    <span className="min-w-0 flex-1 truncate text-sm">
                      {section.name}
                      <span className="ms-2 text-xs text-gray-400">
                        {t("{{position}} of {{total}}", { position: index + 1, total: sections.length })} ·{" "}
                        {chatCount(String(section._id))}
                      </span>
                    </span>
                    <IconComponent
                      name="EditIcon"
                      width="0.9rem"
                      height="0.9rem"
                      toolTipContent={t("Rename section")}
                      onClick={() => setEditing({ id: String(section._id), name: section.name })}
                    />
                    <IconComponent
                      name="DeleteIcon"
                      width="0.9rem"
                      height="0.9rem"
                      toolTipContent={t("Delete section")}
                      onClick={() => void run(() => deleteSectionRequest({ sectionId: section._id }))}
                    />
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      }
    />
  );
}

/** Manage sections for Project Chats (PRD §4.10): drag-only reordering of the user's OTHER projects. */
export function ProjectOrderDialog({ onClose }: { readonly onClose: () => void }) {
  const { t } = useTranslation();
  const chat = usePortalChat();
  const [busy, setBusy] = useState(false);

  const projects = useMemo(() => {
    const byProject = new Map<string, { projectId: string; name: string; count: number }>();
    for (const c of chat.conversations) {
      if (c.type !== "space" || !c.projectId) continue;
      const key = String(c.projectId);
      if (key === chat.currentProjectId) continue; // the current project always sits pinned on top
      const entry = byProject.get(key) ?? { projectId: key, name: c.projectName ?? t("Project"), count: 0 };
      entry.count += 1;
      byProject.set(key, entry);
    }
    const rows = [...byProject.values()];
    rows.sort((a, b) => {
      const ia = chat.projectOrder.indexOf(a.projectId);
      const ib = chat.projectOrder.indexOf(b.projectId);
      if (ia !== -1 || ib !== -1) {
        return (ia === -1 ? Number.MAX_SAFE_INTEGER : ia) - (ib === -1 ? Number.MAX_SAFE_INTEGER : ib);
      }
      return a.name.localeCompare(b.name);
    });
    return rows;
  }, [chat.conversations, chat.projectOrder, chat.currentProjectId, t]);

  const { containerRef, handleProps, dragIndex, overIndex } = useDragReorder((from, to) => {
    if (busy) return;
    const ids = projects.map((p) => p.projectId);
    const [moved] = ids.splice(from, 1);
    if (moved === undefined) return;
    ids.splice(to, 0, moved);
    setBusy(true);
    void updateProjectOrderRequest({ projectIds: ids.map((id) => toObjectIdString(id)) })
      .then(() => chat.refreshList())
      .finally(() => setBusy(false));
  });

  return (
    <ModalStateDialogComponent
      isVisible
      width="26rem"
      headerTitle={t("Manage sections")}
      onCancel={onClose}
      footerContent={<></>}
      modelContent={
        <div className="flex max-h-[60vh] w-full flex-col gap-2">
          <div className="text-xs text-gray-500">
            {t(
              "Your current project always sits at the top as Current Project. The rest follow the order you set here.",
            )}
          </div>
          <div ref={containerRef} className="min-h-0 flex-1 overflow-y-auto">
            {!projects.length && (
              <div className="flex flex-col items-center gap-1 p-4 text-center">
                <span className="text-sm font-semibold text-gray-500">{t("No other projects yet")}</span>
                <span className="text-xs text-gray-400">
                  {t(
                    "Once you are on Spaces in more than one project, their chats appear here and you can set the order.",
                  )}
                </span>
              </div>
            )}
            {projects.map((project, index) => (
              <div
                key={project.projectId}
                data-drag-row
                className={`flex items-center gap-2 border-b border-[#f0f5f9] px-1 py-1.5 ${
                  dragIndex === index ? "opacity-50" : ""
                } ${overIndex === index && dragIndex !== null && dragIndex !== index ? "border-t-2 border-t-[#6BBBE3]" : ""} ${
                  dragIndex !== null && overIndex !== null && index === overIndex - 1 && overIndex > dragIndex + 1
                    ? "border-b-2 border-b-[#6BBBE3]"
                    : ""
                }`}
              >
                <span {...handleProps(index)} title={t("Drag a section by its handle to reorder.")}>
                  <IconComponent name="threedots" width="0.9rem" height="0.9rem" className="rotate-90 opacity-50" />
                </span>
                <span className="min-w-0 flex-1 truncate text-sm">
                  {project.name}
                  <span className="ms-2 text-xs text-gray-400">
                    {t("{{position}} of {{total}}", { position: index + 1, total: projects.length })} · {project.count}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      }
    />
  );
}

/**
 * "New section" dialog (PRD §4.9): single required name field, Create disabled while empty,
 * toast "Section ‹name› created". When opened from a chat row's Move-to-section submenu the
 * fresh section immediately receives that chat.
 */
export function NewSectionDialog({
  scope,
  assignConversationId,
  onClose,
}: {
  readonly scope: "direct" | "group";
  readonly assignConversationId?: string | undefined;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const chat = usePortalChat();
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  const create = async () => {
    const trimmed = name.trim();
    if (!trimmed || busy) return;
    setBusy(true);
    try {
      const response = await createSectionRequest({ name: trimmed, scope });
      if (response.data.status && response.data.result) {
        if (assignConversationId) {
          await chat.updateConversationState(assignConversationId, {
            sectionId: String(response.data.result._id),
          });
        }
        await chat.refreshList();
        chat.showToast(t("Section {{name}} created", { name: trimmed }));
        onClose();
      } else if (!response.data.status) {
        chat.showServerError(response.data.errorMessage, t("Something went wrong"));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalStateDialogComponent
      isVisible
      width="24rem"
      headerTitle={t("New section")}
      onCancel={onClose}
      modelContent={
        <div className="flex w-full flex-col gap-2">
          <label className="text-xs text-gray-500">{t("Section name")}</label>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={SECTION_NAME_MAX_LENGTH}
            className="w-full rounded-xs border border-[#dce6f1] px-2 py-1 text-sm outline-hidden"
            onKeyDown={(e) => {
              if (e.key === "Enter") void create();
            }}
          />
        </div>
      }
      footerContent={
        <div className="flex justify-end gap-2">
          <ButtonComponent buttonType="borderButton" onClick={onClose}>
            {t("Cancel")}
          </ButtonComponent>
          <ButtonComponent buttonType="gradientButton" disabled={!name.trim() || busy} onClick={() => void create()}>
            {t("Create")}
          </ButtonComponent>
        </div>
      }
    />
  );
}

/** Hide confirmation — title and per-type body copy from PRD §4.11. */
export function HideConfirmDialog({
  conversation,
  onClose,
}: {
  readonly conversation: ChatConversation;
  readonly onClose: () => void;
}) {
  const { t } = useTranslation();
  const chat = usePortalChat();

  const body =
    conversation.type === "space"
      ? t(
          "{{name}} will be removed from your sidebar. It reappears in Current Project whenever you are working in {{projectName}}, and hides again when you move to another project.",
          { name: conversation.name, projectName: conversation.projectName ?? "" },
        )
      : t(
          "{{name}} will be removed from your sidebar. Your history stays intact and you remain a member. Search for it any time to bring it back, and it reappears automatically when someone sends a new message.",
          { name: conversation.name },
        );

  return (
    <ModalStateDialogComponent
      isVisible
      width="26rem"
      headerTitle={t("Hide this conversation?")}
      onCancel={onClose}
      modelContent={<div className="text-sm">{body}</div>}
      footerContent={
        <div className="flex justify-end gap-2">
          <ButtonComponent buttonType="borderButton" onClick={onClose}>
            {t("Cancel")}
          </ButtonComponent>
          <ButtonComponent
            buttonType="gradientButton"
            onClick={() => {
              void chat.updateConversationState(conversation._id, { hidden: true });
              chat.showToast(t("{{name}} hidden — search to bring it back.", { name: conversation.name }));
              // PRD §4.11: the panel auto-selects the next available conversation.
              if (chat.selectedId === conversation._id) chat.selectNextAfter(conversation._id);
              onClose();
            }}
          >
            {t("Hide")}
          </ButtonComponent>
        </div>
      }
    />
  );
}
